import Phaser from "phaser";
import type { GameContext } from "../GameContext";
import { BootController } from "../controllers/BootController";

/**
 * Per Part 3 (M16): "BootScene -> LoginScene -> CharacterSelectScene ->
 * in-world scenes". This scene's only job is: run BootController (which
 * loads the asset manifest and — per 2.11 — ALWAYS reaches
 * "unauthenticated" regardless of whether that load succeeded), show a
 * loading bar, then hand off to LoginScene. It never blocks on art.
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super("BootScene");
  }

  private get ctx(): GameContext {
    return this.game.registry.get("ctx") as GameContext;
  }

  async create(): Promise<void> {
    const { width, height } = this.cameras.main;
    const cx = width / 2;
    const cy = height / 2;

    this.cameras.main.setBackgroundColor("#0f1826");

    this.load.svg("boot-logo", "/art/logo/aetheria-logo-icon.svg", { width: 96, height: 96 });
    this.load.once("complete", () => {
      this.add.image(cx, cy - 70, "boot-logo").setOrigin(0.5);
    });
    this.load.start();

    this.add
      .text(cx, cy - 10, "AETHERIA", { fontFamily: "Baloo 2, sans-serif", fontSize: "34px", color: "#fbf3e3" })
      .setOrigin(0.5);
    this.add
      .text(cx, cy + 22, "CHRONICLES OF THE SHATTERED VEIL", {
        fontFamily: "Inter, sans-serif", fontSize: "11px", color: "#a9b4c9", letterSpacing: 2,
      })
      .setOrigin(0.5);

    const barWidth = 260;
    const barY = cy + 70;
    const barTrack = this.add.rectangle(cx, barY, barWidth, 6, 0x10151f).setStrokeStyle(1, 0x23324a);
    const barFill = this.add
      .rectangle(cx - barWidth / 2, barY, 4, 6, 0xf5a93f)
      .setOrigin(0, 0.5);
    const loadingText = this.add
      .text(cx, barY + 20, "Loading world data...", { fontFamily: "Inter, sans-serif", fontSize: "12px", color: "#a9b4c9" })
      .setOrigin(0.5);

    this.tweens.add({
      targets: barFill,
      width: barWidth,
      duration: 900,
      ease: "Sine.easeInOut",
      repeat: -1,
      yoyo: true,
    });

    const controller = new BootController(this.ctx);
    const result = await controller.run();

    this.tweens.killTweensOf(barFill);

    if (!result.manifestLoaded) {
      // 2.11: degrade, don't block. Show a brief, dismissible notice and
      // proceed to LoginScene regardless.
      loadingText.setText("Some art may be missing — continuing anyway...");
      loadingText.setColor("#e85d4e");
      this.time.delayedCall(1200, () => this.scene.start("LoginScene"));
      return;
    }

    loadingText.destroy();
    barTrack.destroy();
    barFill.destroy();
    this.scene.start("LoginScene");
  }
}
